/** @format */

import { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { LuPencil, LuShieldAlert, LuTrash2 } from "react-icons/lu";
import { fetchVehicleClaim } from "../../../shared/services/simpleListItemsService";
import Claim from "../../../shared/models/claimModel";
import { deformatDateT, formatDateT } from "../../../shared/utils/formatters";
import RadioGroup from "../../../shared/components/RadioGroup";
import Button from "../../../shared/components/Button";
import TqClaimModal from "./tqClaimModal";

const TqAddDriverClaim = ({
  onClaimsUpdate,
  localClaimFields,
  setLocalClaimFields,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext();
  const claimMethods = useForm({ mode: "onChange" });
  const [vClaim, setVClaim] = useState([]);
  const [isClaimTypesLoading, setIsClaimTypesLoading] = useState(true);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const maxClaimsAllowed = 5;

  useEffect(() => {
    fetchVehicleClaim()
      .then((statuses) => setVClaim(statuses))
      .catch((error) => console.error("Error fetching claim types:", error))
      .finally(() => setIsClaimTypesLoading(false));
  }, []);

  const getClaimDescription = (typeId) =>
    vClaim.find((type) => type.listItemId == typeId)?.listItemDescription || "";

  const handleAddClaim = () => {
    if (localClaimFields.length >= maxClaimsAllowed) return;

    claimMethods.reset({
      claimIndex: null,
      claimType: null,
      AddClaim_Date: null,
      claimCost: "",
      Injured: null,
      WhosFault: null,
    });
    setClaimModalOpen(true);
  };

  const handleClaimEdit = (claim, index) => {
    claimMethods.reset({
      claimIndex: index,
      claimType: {
        value: claim.typeId,
        label: getClaimDescription(claim.typeId),
      },
      AddClaim_Date: deformatDateT(claim.claimDate),
      claimCost: String(claim.cost ?? ""),
      Injured: claim.injuryInvolved ? "Yes" : "No",
      WhosFault: claim.atFault ? "Yes" : "No",
    });
    setClaimModalOpen(true);
  };

  const handleClaimSaved = (claimData) => {
    const updatedClaim = new Claim(
      claimData.claimType?.value,
      formatDateT(claimData.AddClaim_Date),
      Number(claimData.claimCost),
      claimData.WhosFault === "Yes",
      claimData.Injured === "Yes",
      null,
      null,
      false,
      null,
      false,
      false,
      null,
      null,
      0,
    );

    const updatedClaims =
      claimData.index !== null && claimData.index !== undefined
        ? localClaimFields.map((claim, index) =>
            index === claimData.index ? updatedClaim : claim,
          )
        : [...localClaimFields, updatedClaim];

    setLocalClaimFields(updatedClaims);
    onClaimsUpdate(updatedClaims);
  };

  const handleClaimDelete = (index) => {
    const updatedClaims = localClaimFields.filter(
      (_, claimIndex) => claimIndex !== index,
    );
    setLocalClaimFields(updatedClaims);
    onClaimsUpdate(updatedClaims);
  };

  const handleNoClaim = () => {
    setValue("AdditionalClaim", "No");
    setLocalClaimFields([]);
    onClaimsUpdate([]);
  };

  return (
    <>
      <RadioGroup
        name="AdditionalClaim"
        mainLabel="Have you had any accidents, losses, thefts, fire, flood or claims in any vehicle regardless of fault or whether you claimed or not in the last 5 years?"
        register={register}
        trigger={trigger}
        errors={errors.AdditionalClaim}
        onNo={handleNoClaim}
      />

      {watch("AdditionalClaim") === "Yes" && (
        <div className="tw-flex tw-flex-col">
          {localClaimFields.map((claim, index) => (
            <div
              className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-dis-divider tw-py-3"
              key={
                claim.claimDetailsId ??
                `${claim.typeId}-${claim.claimDate}-${index}`
              }
            >
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
                {isClaimTypesLoading ? (
                  <span
                    className="tw-h-6 tw-w-6 tw-shrink-0 tw-animate-pulse tw-rounded-full tw-bg-dis-field"
                    aria-hidden="true"
                  />
                ) : (
                  <LuShieldAlert
                    className="tw-h-6 tw-w-6 tw-shrink-0 tw-text-dis-navy"
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                )}
                <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
                  {isClaimTypesLoading ? (
                    <span
                      className="tw-h-5 tw-w-32 tw-animate-pulse tw-rounded tw-bg-dis-field"
                      aria-hidden="true"
                    />
                  ) : (
                    <span className="tw-text-[17px] tw-font-semibold tw-text-dis-navy">
                      {getClaimDescription(claim.typeId)}
                    </span>
                  )}
                  <span className="tw-text-dis-small tw-text-dis-text-muted">
                    {deformatDateT(claim.claimDate)}
                  </span>
                </div>
              </div>
              <div className="tw-flex tw-shrink-0">
                <button
                  type="button"
                  onClick={() => handleClaimEdit(claim, index)}
                  className="tw-mr-1.5 tw-flex tw-h-[42px] tw-w-[42px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-dis-field-hover tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                  aria-label={`Edit ${getClaimDescription(claim.typeId)} claim`}
                >
                  <LuPencil
                    className="tw-h-5 tw-w-5"
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => handleClaimDelete(index)}
                  className="tw-flex tw-h-[42px] tw-w-[42px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-dis-field-hover tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                  aria-label={`Delete ${getClaimDescription(
                    claim.typeId,
                  )} claim`}
                >
                  <LuTrash2
                    className="tw-h-5 tw-w-5"
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          ))}

          {localClaimFields.length < maxClaimsAllowed ? (
            <div className="tw-mt-5 tw-mb-5 tw-flex">
              <Button
                type="button"
                variant="soft"
                size="small"
                onClick={handleAddClaim}
              >
                Add a Claim
              </Button>
            </div>
          ) : (
            <div
              className="tw-mt-3 tw-border-0 tw-border-l-4 tw-border-solid tw-border-dis-error tw-py-2 tw-pl-3 tw-text-base tw-font-bold tw-text-dis-error"
              role="alert"
            >
              You’ve reached the maximum of 5 claims.
            </div>
          )}
        </div>
      )}

      <FormProvider {...claimMethods}>
        <TqClaimModal
          onSaveClaim={handleClaimSaved}
          isModalOpen={claimModalOpen}
          setIsModalOpen={setClaimModalOpen}
        />
      </FormProvider>
    </>
  );
};

export default TqAddDriverClaim;
